import { Link } from 'react-router-dom';
import { Award, Check, Download, RotateCcw } from 'lucide-react';
import type { TrainingSubmitResult } from '../../../hooks/useTrainingPlayer';
import { Button } from '../../ui/Button';
import { Pill } from '../../ui/Pill';

/**
 * End-of-attempt screen. Deliberately score-only on a fail: which
 * questions were wrong is never shown (full_retake modules restart from
 * the first slide, per the agreed assessment model).
 *
 * An unmarked assessment has no score, no pass mark and no certificate, so
 * it gets a plain acknowledgement instead.
 */
export function ResultScreen({
  result,
  taskId,
  backTo = '/me',
  backLabel = 'Back to your onboarding',
  onRetake,
}: {
  result: TrainingSubmitResult;
  taskId: string;
  /** Where the finish button goes; the training portal has no /me. */
  backTo?: string;
  backLabel?: string;
  onRetake: () => void;
}) {
  if (result.graded === false) {
    return (
      <div className="flex flex-col items-center gap-4 py-8 text-center">
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-success-soft/15 text-success-soft-fg">
          <Check className="h-9 w-9" />
        </span>
        <Pill tone="success">Complete</Pill>
        <p className="max-w-sm text-sm text-fg-muted">
          Thank you. Your answers have been recorded and this assessment is complete.
        </p>
        <Link to={backTo}>
          <Button variant="success">{backLabel}</Button>
        </Link>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-4 py-8 text-center">
      <span
        className={`flex h-20 w-20 items-center justify-center rounded-full text-2xl font-bold tabular-nums ${
          result.passed
            ? 'bg-success-soft/15 text-success-soft-fg'
            : 'bg-warning-soft/15 text-warning-soft-fg'
        }`}
      >
        {Math.round(result.score)}%
      </span>
      <Pill tone={result.passed ? 'success' : 'warning'}>
        {result.passed ? 'Passed' : 'Not passed'}
      </Pill>
      <p className="max-w-sm text-sm text-fg-muted">
        {result.passed
          ? `You scored ${result.score}% — the pass mark is ${result.pass_mark}%. Well done, this training is complete.`
          : `You scored ${result.score}% — the pass mark is ${result.pass_mark}%. You will need to retake the training from the start.`}
      </p>
      {result.sections.length > 0 ? (
        <ul className="w-full max-w-sm space-y-1 text-left text-sm">
          {result.sections.map((s) => (
            <li
              key={s.section_id}
              className="flex items-center justify-between gap-2 rounded-token-sm bg-surface-2 px-3 py-2"
            >
              <span className="truncate text-fg-muted">{s.title}</span>
              {s.not_applicable ? (
                <Pill tone="neutral" size="sm">
                  Not assessed
                </Pill>
              ) : (
                <span className="flex items-center gap-2">
                  <span className="tabular-nums text-fg-dim">{Math.round(s.score)}%</span>
                  <Pill tone={s.passed ? 'success' : 'warning'} size="sm">
                    {s.passed ? 'Passed' : 'Not passed'}
                  </Pill>
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {result.passed ? (
        <div className="flex flex-col items-center gap-3">
          <a
            href={`/api/me/training/${taskId}/certificate.pdf`}
            className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
          >
            <Download className="h-4 w-4" />
            Download your certificate
          </a>
          <p className="flex items-center gap-1.5 text-xs text-fg-dim">
            <Award className="h-3.5 w-3.5" />A copy is also being emailed to you.
          </p>
          <Link to={backTo}>
            <Button variant="success">{backLabel}</Button>
          </Link>
        </div>
      ) : (
        <Button variant="warning" iconLeft={<RotateCcw className="h-4 w-4" />} onClick={onRetake}>
          Retake from the start
        </Button>
      )}
    </div>
  );
}
